import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, Instagram, Mail, Package, Truck } from "lucide-react";
import { getOrder, inr } from "@/api";
import { useCart } from "@/context/CartContext";
import { FadeIn, LotusMark } from "@/components/Reveal";

const OrderSuccess = () => {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [failed, setFailed] = useState(false);
  const { clear } = useCart();

  useEffect(() => {
    clear();
    let cancelled = false;

    const load = async () => {
      try {
        const data = await getOrder(orderId);
        if (cancelled) return;
        setOrder(data);
      } catch {
        if (!cancelled) setFailed(true);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  if (failed) {
    return (
      <div data-testid="order-not-found" className="pt-40 pb-32 text-center px-6">
        <p className="font-display italic text-3xl text-maroon-deep/70">We couldn't find that order.</p>
        <p className="mt-3 text-sm text-clay">If you placed an order recently, please contact us and we'll help locate it.</p>
        <a
          href="mailto:hello@vaiyra.in"
          className="mt-6 inline-flex items-center gap-2 text-xs tracking-[0.3em] uppercase text-gold-dark border-b border-gold pb-1"
        >
          <Mail className="w-3.5 h-3.5" /> hello@vaiyra.in
        </a>
      </div>
    );
  }

  const isPaid = order?.payment_method === "razorpay" && order?.payment_status === "paid";
  const paymentLabel = order?.payment_method === "cod"
    ? "Cash on Delivery"
    : isPaid
      ? "Payment confirmed"
      : "Payment received";

  return (
    <div data-testid="order-success-page" className="pt-28 sm:pt-32 pb-24 px-5 sm:px-6">
      <div className="max-w-2xl mx-auto text-center">
        <motion.div
          initial={{ scale: 0, rotate: -30 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: "spring", damping: 14, stiffness: 200, delay: 0.15 }}
          className="mx-auto w-20 h-20 sm:w-24 sm:h-24 rounded-full border border-gold/50 bg-ivory-card flex items-center justify-center shadow-[0_20px_60px_rgba(74,18,26,0.15)]"
        >
          <LotusMark className="w-9 h-9 sm:w-10 sm:h-10 text-gold-dark" />
        </motion.div>

        <FadeIn delay={0.3}>
          <p className="mt-7 sm:mt-8 text-[10px] sm:text-xs tracking-[0.4em] uppercase text-gold-dark">With Grace, It's Yours</p>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl text-maroon-deep">Order Confirmed</h1>
          {order && (
            <p data-testid="order-success-id" className="mt-4 font-brand tracking-[0.2em] text-base sm:text-lg text-maroon">
              {order.order_number}
            </p>
          )}
          <p className="mt-4 text-sm sm:text-base text-clay leading-relaxed max-w-xl mx-auto">
            {order
              ? `Thank you, ${order.customer.name.split(" ")[0]} — your pieces are being prepared with care. We'll send your order and delivery updates to ${order.customer.email}.`
              : "Preparing your order details…"}
          </p>
        </FadeIn>

        {order && (
          <FadeIn delay={0.42} className="mt-9 sm:mt-12 text-left">
            <div className="grid grid-cols-3 border-y border-gold/20 py-5">
              <div className="text-center px-2">
                <div className="mx-auto w-8 h-8 rounded-full bg-maroon text-ivory flex items-center justify-center">
                  <Check className="w-4 h-4" />
                </div>
                <p className="mt-2 text-[9px] sm:text-[10px] tracking-[0.18em] uppercase text-maroon-deep">Confirmed</p>
              </div>
              <div className="text-center px-2">
                <div className="mx-auto w-8 h-8 rounded-full border border-gold bg-ivory-card text-gold-dark flex items-center justify-center">
                  <Package className="w-4 h-4" />
                </div>
                <p className="mt-2 text-[9px] sm:text-[10px] tracking-[0.18em] uppercase text-clay">Preparing</p>
              </div>
              <div className="text-center px-2">
                <div className="mx-auto w-8 h-8 rounded-full border border-maroon/15 bg-ivory text-clay flex items-center justify-center">
                  <Truck className="w-4 h-4" />
                </div>
                <p className="mt-2 text-[9px] sm:text-[10px] tracking-[0.18em] uppercase text-clay">Delivered</p>
              </div>
            </div>
          </FadeIn>
        )}

        {order && (
          <FadeIn delay={0.5} className="mt-8 bg-ivory-card border border-gold/30 p-5 sm:p-8 text-left">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-[10px] tracking-[0.4em] uppercase text-gold-dark">Your Order</p>
                <p className="mt-1 text-xs text-clay">{paymentLabel}</p>
              </div>
              <span data-testid="order-success-total" className="font-display text-2xl text-maroon-deep">{inr(order.total)}</span>
            </div>

            <ul className="mt-6 space-y-4">
              {order.items.map((i, idx) => (
                <li key={idx} className="flex justify-between gap-4 text-sm">
                  <span className="text-maroon-deep">
                    {i.name} <span className="text-clay/70">· {i.size} × {i.qty}</span>
                  </span>
                  <span className="text-maroon-deep font-display shrink-0">{inr(i.price * i.qty)}</span>
                </li>
              ))}
            </ul>

            <div className="mt-6 pt-5 border-t border-gold/25">
              <p className="text-[10px] tracking-[0.3em] uppercase text-gold-dark">Delivery</p>
              <p className="mt-2 text-sm text-maroon-deep">Dispatched in 2–3 working days</p>
              <p className="mt-1 text-xs text-clay">Expected delivery across India: 5–7 working days.</p>
              <p className="mt-3 text-xs text-clay/80">
                Delivering to {order.customer.address}, {order.customer.city}, {order.customer.state} — {order.customer.pincode}
              </p>
            </div>
          </FadeIn>
        )}

        <FadeIn delay={0.58} className="mt-7 sm:mt-8 p-5 border border-maroon/10 bg-ivory-card/60">
          <p className="text-sm text-maroon-deep">Need help with your order?</p>
          <p className="mt-1 text-xs text-clay leading-relaxed">
            We're here if you need us. Reach VAIYRA at{" "}
            <a href="mailto:hello@vaiyra.in" className="text-gold-dark hover:text-maroon transition-colors">
              hello@vaiyra.in
            </a>
            {" "}or message us on Instagram.
          </p>
        </FadeIn>

        <FadeIn delay={0.66} className="mt-9 flex flex-col sm:flex-row justify-center gap-3 sm:gap-5">
          <Link
            to="/track"
            data-testid="order-success-track-button"
            className="inline-flex items-center justify-center gap-3 bg-maroon text-ivory px-7 sm:px-8 py-4 text-[10px] sm:text-xs tracking-[0.3em] uppercase hover:bg-maroon-hover transition-colors duration-300"
          >
            Track Your Order <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/shop"
            data-testid="order-success-shop-button"
            className="inline-flex items-center justify-center gap-3 border border-maroon/25 px-7 sm:px-8 py-4 text-[10px] sm:text-xs tracking-[0.3em] uppercase text-maroon-deep hover:border-gold hover:text-gold-dark transition-colors duration-300"
          >
            Continue Shopping
          </Link>
          <a
            href="https://instagram.com/vaiyra.in"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="order-success-instagram-link"
            className="inline-flex items-center justify-center gap-3 border border-maroon/15 px-7 sm:px-8 py-4 text-[10px] sm:text-xs tracking-[0.3em] uppercase text-maroon-deep hover:border-gold hover:text-gold-dark transition-colors duration-300"
          >
            <Instagram className="w-4 h-4" /> @vaiyra.in
          </a>
        </FadeIn>
      </div>
    </div>
  );
};

export default OrderSuccess;
